package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCardPaddingCompactVar
import ai.wagemix.web.theme.WMCardPaddingVar
import ai.wagemix.web.theme.WMCardTitleTextStyle
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMIconSizeVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMNoticeIconGapVar
import ai.wagemix.web.theme.WMPanelCornerVar
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.FSBreakpoint
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle

/**
 * Penpot `Wagemix / Renewal Banner`: `Size: Compact` (the action under the text) below Medium, `Large` (the action
 * at the end of the row) from Medium up.
 */
val WMRenewalBannerStyle = CssStyle {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(WMInlineGapVar.value())
            .padding(WMCardPaddingCompactVar.value())
            .borderRadius(WMPanelCornerVar.value())
            .backgroundColor(colors.tertiaryContainer)
            .color(colors.onTertiaryContainer)
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.tertiaryContainer)
    }
    FSBreakpoint.Medium {
        Modifier.flexDirection(FlexDirection.Row).alignItems(AlignItems.Center).padding(WMCardPaddingVar.value())
    }
}

/**
 * Penpot `Wagemix / Renewal Banner`: the reminder a lapsed account sees on every app page (paid-feature plan W7): a
 * card icon, [title], [message] and one [actionLabel] button that calls [onAction].
 */
@Composable
fun WMRenewalBanner(
    title: String,
    message: String,
    actionLabel: String,
    modifier: Modifier = Modifier,
    onAction: () -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    Column(modifier = WMRenewalBannerStyle.toModifier().role("status").then(modifier)) {
        Row(modifier = Modifier.fillMaxWidth().gap(WMNoticeIconGapVar.value()), verticalAlignment = Alignment.Top) {
            WMIcon(
                WMIconGlyph.CreditCard,
                accessibilityString = null,
                modifier = Modifier.flexShrink(0).color(colors.tertiary),
                size = WMIconSizeVar.value()
            )
            Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value())) {
                SpanText(title, WMCardTitleTextStyle.toModifier())
                SpanText(message, WMBodySmallTextStyle.toModifier())
            }
        }
        WMButton(actionLabel, onClick = onAction)
    }
}
